//#region Imports
import React, { useState, useEffect } from "react";
import { StyleSheet, Platform, TouchableOpacity, Text, SafeAreaView, FlatList, Pressable, ScrollView, View, useWindowDimensions } from "react-native";
import { useTheme, useNavigation } from '@react-navigation/native';
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import { Card, Checkbox, Provider } from 'react-native-paper';
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import { useClients } from "../../../hooks/useClients";
import { useTranslation } from "react-i18next";
import { showMessage } from "react-native-flash-message";
import CustomDropDownWithIcons from "../../../components/CustomDropDownWithIcons";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import Swal from 'sweetalert2';
import CustomPaging from "../../../components/CustomPaging/CustomPagining";
import { generateUniqueID } from "../../../components/SurveyBuilderComponent/CreateSurvey/CreateSurveyFunctions";

const HRAdminClientProjectDetails = ({ client, selectClientsDetailsScreen }) => {
    //#region Const Definitions
    const { width, height } = useWindowDimensions();
    const { t } = useTranslation();
    const navigation = useNavigation();
    const { colors } = useTheme();
    const styles = makeStyles(colors, width, height);
    const { isLoading, error, GetProjects, GetHelpers, InsertProject, UpdateProject, DeleteProject } = useClients();

    // Current filled in values
    const [phasecode, setPhasecode] = useState('');
    const [description, setDescription] = useState('');
    const [active, setActive] = useState(false);
    const [chargeable, setChargeable] = useState(false);
    const [isPayable, setIsPayable] = useState(false);
    const [partnerId, setPartnerId] = useState('');
    const [managerId, setManagerId] = useState('');
    const [clientProjects, setClientProjects] = useState([]);
    const [managerOptions, setManagerOptions] = useState([]);
    const [partnerOptions, setPartnerOptions] = useState([]);

    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [editingProjectPhaseCodeUI, setEditingProjectPhaseCodeUI] = useState(null);
    const [editingProjectClmgrUI, setEditingProjectClmgrUI] = useState(null);
    const [expandedProjects, setExpandedProjects] = useState({});

    //#region Paging Menu
    const [pagedItems, setPagedItems] = useState([]);

    //#region UseEffect
    useEffect(() => {
        GetClientProjects();
        GetHelpers().then((res) => {
            setPartnerOptions(res.partnerOptions);
            setManagerOptions(res.managerOptions);
        })
            .catch((error) => {
                showMessage({
                    message: t("common:error"),
                    type: "danger",
                    duration: 3000,
                });
            })
    }, []);

    const GetClientProjects = () => {
        GetProjects(client).then((res) => {
            setClientProjects(res);
        })
            .catch((error) => {
                showMessage({
                    message: t("common:error"),
                    type: "danger",
                    duration: 3000,
                });
            })
    }
    //#endregion

    //#region Handle Cancel/Delete/Save/Edit
    const handleCancel = () => {
        setDescription('');
        setPhasecode('');
        setActive(false);
        setChargeable(false);
        setIsPayable(false);
        setPartnerId('');
        setManagerId('');
        setIsEditing(false);
        setEditingProjectClmgrUI(null);
        setEditingProjectPhaseCodeUI(null);
        showMessage({
            message: t("common:formReset"),
            type: "info",
            duration: 2000,
        });
    };

    const handleDelete = (phasecode) => {
        Swal.fire({
            title: t('common:areusure'),
            text: t('common:deleteProjectConfirmation'),
            icon: "question",
            showCancelButton: true,
            reverseButtons: true,
            cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">Cancel</text>`,
            cancelButtonColor: '#d3d3d3',
            confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">Delete</text>`,
            confirmButtonColor: '#A0A2FF',
        }).then((result) => {
            if (result.isConfirmed) {
                DeleteProject(phasecode).then((res) => {
                    if (res === 200) {
                        showMessage({
                            message: t("common:deleteSuccessP"),
                            type: "success",
                            duration: 3000,
                        });
                        GetClientProjects();
                    }
                }).catch(() => {
                    showMessage({
                        message: t("common:error"),
                        type: "danger",
                        duration: 3000,
                    });
                });
            }
        });
    }

    const handleSaveOrUpdate = async () => {
        if (!description.trim() || !phasecode.trim() || !managerId || !partnerId) {
            showMessage({
                message: t("common:pleaseFillAllFields"),
                type: "warning",
                duration: 3000,
            });
            return;
        }

        var saveSuccess = true; 
        let savePromises = [];

        if (phasecode.length > 7) {
            showMessage({
                message: t("common:codeMaxLengthWarning"),
                type: "warning",
                duration: 3000,
            });
            return;
        }

        if (isEditing) {
            // Update existing client
            const updatedProject = {
                phasecode: phasecode,
                description: description,
                active: active,
                chargeable: chargeable,
                isPayable: isPayable,
                managerName: '',
                managerId: managerId,
                partnerName: '',
                partnerId: partnerId,
                clmgrUI: editingProjectClmgrUI,
                phasecodeUI: editingProjectPhaseCodeUI
            };

            savePromises.push(UpdateProject(updatedProject).then((res) => {
                if (res === 200) {
                    showMessage({
                        message: t("common:projUpdatedSuccessfully"),
                        type: "success",
                        duration: 3000,
                    });
                    GetClientProjects();
                }
                }).catch(() => {
                    saveSuccess = false;
                    showMessage({
                        message: t("common:error"),
                        type: "danger",
                        duration: 3000,
                    });
                })
            )

        } else {
            // Insert new project
            const newProject = {
                phasecode: phasecode,
                description: description,
                active: active,
                chargeable: chargeable,
                isPayable: isPayable,
                managerName: '',
                managerId: managerId,
                partnerName: '',
                partnerId: partnerId,
                clmgrUI: generateUniqueID(),
                phasecodeUI: generateUniqueID()
            };

            savePromises.push(InsertProject(newProject, client.jobno).then((res) => {
                if (res === 200) {
                    showMessage({
                        message: t("common:projectCreatedSuccessfully"),
                        type: "success",
                        duration: 3000,
                    });
                    GetClientProjects();
                } else {
                    saveSuccess = false;
                    showMessage({
                        message: t("common:insertError"),
                        type: "danger",
                        duration: 3000,
                    });
                }
                }).catch(() => {
                    saveSuccess = false;
                    showMessage({
                        message: t("common:insertError"),
                        type: "danger",
                        duration: 3000,
                    });
                })
            )
        }

        await Promise.all(savePromises);
        if(saveSuccess){
            // Reset form and editing state
            handleCancel();
            setIsEditing(false);
            setEditingProjectClmgrUI(null);
            setEditingProjectPhaseCodeUI(null);
        }
    };

    const handleEdit = (project) => {
        setDescription(project.description);
        setPhasecode(project.phasecode);
        setActive(project.active);
        setChargeable(project.chargeable);
        setIsPayable(project.isPayable);
        setPartnerId(project.partnerId);
        setManagerId(project.managerId);
        setEditingProjectPhaseCodeUI(project.phasecodeUI);
        setEditingProjectClmgrUI(project.clmgrUI);
        setIsEditing(true);
    };
    

    //#region Rendering Projects
    const toggleExpand = (phasecode) => {
        setExpandedProjects(prev => ({
            ...prev,
            [phasecode]: !prev[phasecode]
        }));
    };

    // Render each project line
    const renderProject = (project) => {
        const isExpanded = expandedProjects[project.phasecode];

        return (
        <View key={project.phasecode} style={styles.showHide}>
            <Pressable onPress={() => toggleExpand(project.phasecode)}>
                <View key={project.phasecode}>
                    <View style={styles.row}>
                        <CustomLabel
                            value={project.description}
                            type='AVAILABLEUSED'
                        />
                        <CustomLabel
                            value={project.phasecode}
                            type='AVAILABLEUSED'
                        />
                        <CustomLabel
                            value={project.active ? t('common:active') : t('common:Inactive')}
                            type='AVAILABLEUSED'
                        />
                        <CustomLabel
                            value={project.chargeable ? t('common:chargable') : t('common:nonChargable')}
                            type='AVAILABLEUSED'
                        />
                        <CustomButton
                            iconName='pencil-outline'
                            onPress={() => handleEdit(project)}
                            show='HOME_BELL'
                            type='HOME_BELL'
                            textTop='2'
                            sz={20}
                        />
                        <CustomButton
                            onPress={() => handleDelete(project.phasecode)}
                            iconName="trash-outline"
                            show="HOME_BELL"
                            type="HOME_BELL"
                            textTop="2"
                            sz={20}
                        />
                    </View>
                </View>
            </Pressable>
            {isExpanded && (
                <SafeAreaView style={styles.details}>
                    <SafeAreaView style={styles.item}>
                        <View style={styles.line} />
                        <View style={styles.row}>
                            <CustomLabel value={t('common:isPayable') + ':'} type='TSDETAILSMINOR' />
                            <CustomLabel value={project.isPayable ? t('common:yes') : t('common:no')} type='TSDETAILSMINOR' />
                        </View>
                        <View style={styles.line} />
                        <View style={styles.row}>
                            <CustomLabel value={t('common:managerName') + ':'} type='TSDETAILSMINOR' />
                            <CustomLabel value={project.managerName || '-'} type='TSDETAILSMINOR' />
                        </View>
                        <View style={styles.line} />
                        <View style={styles.row}>
                            <CustomLabel value={t('common:partnerName') + ':'} type='TSDETAILSMINOR' />
                            <CustomLabel value={project.partnerName || '-'} type='TSDETAILSMINOR' />
                        </View>
                        <View style={styles.line} />
                    </SafeAreaView>
                </SafeAreaView>
            )}
        </View>
        );
    };

    // Render all projects
    const renderProjects = ({item: project}) => {
        return (
            <Provider>
                    <Provider key={project.phasecode}>
                        <Card style={expandedProjects[project.phasecode] ? styles.cardstyleOpen : styles.cardstyle}>
                            <Card.Content>
                                {renderProject(project)}
                            </Card.Content>
                        </Card>
                    </Provider>
            </Provider>
        );
    };
    //#endregion

    return (
        <SafeAreaView style={styles.scroll}>
            <SafeAreaView style={styles.main}>
                <View style={{flexDirection: 'row'}}>
                    {/* Back button */}
                    <TouchableOpacity onPress={() => { selectClientsDetailsScreen(); }} style={styles.backButton}>
                        <Ionicons name="arrow-back" size={25} color="black" />
                    </TouchableOpacity>
                    <CustomLabel
                        value={t('navigate:CP') + ' - ' + client.description + ' ' + client.jobno}
                        type='MINORTITLE'
                    />
                </View>
                <SafeAreaView style={styles.row}>
                    {/* Left column: create/edit form card */}
                    <ScrollView style={styles.leftColumn}>
                        <Card style={styles.cardstyle}>
                            <Card.Content>
                                <CustomLabel
                                    value={isEditing ? t('common:editProject') : t('common:createNewProject')}
                                    type="SUBTITLE"
                                    style={{ marginBottom: 12 }}
                                />
                                <View style={styles.line} />
                                <View style={{ marginTop: 20 }}>
                                    <CustomInput
                                        value={phasecode}
                                        setValue={setPhasecode}
                                        placeholder={t('common:phasecode')}
                                        type="MAININPUT"
                                    />
                                </View>

                                <View style={{ marginTop: 20 }}>
                                    <CustomInput
                                        value={description}
                                        setValue={setDescription}
                                        placeholder={t('common:description')}
                                        type="MAININPUT"
                                    />
                                </View>

                                <View style={{flexDirection: width < 1300 ? 'column' : 'row'}}>
                                <View style={styles.CheckBoxRow}>
                                    <CustomLabel value={t('common:active')} type='CHECKBOXLABEL' />
                                    <Checkbox
                                        status={active ? 'checked' : 'unchecked'}
                                        onPress={() => setActive(!active)}
                                    />
                                </View>

                                <View style={styles.CheckBoxRow}>
                                    <CustomLabel value={t('common:chargable')} type='CHECKBOXLABEL' />
                                    <Checkbox
                                        status={chargeable ? 'checked' : 'unchecked'}
                                        onPress={() => setChargeable(!chargeable)}
                                    />
                                </View>

                                <View style={styles.CheckBoxRow}>
                                    <CustomLabel value={t('common:isPayable')} type='CHECKBOXLABEL' />
                                    <Checkbox
                                        status={isPayable ? 'checked' : 'unchecked'}
                                        onPress={() => setIsPayable(!isPayable)}
                                    />
                                </View>
                                </View>

                                <View style={{ marginTop: 20, zIndex: 10000 }}>
                                    <CustomDropDownWithIcons
                                        placeholder={t('common:partnerName')}
                                        defValue={partnerId}
                                        options={partnerOptions}
                                        value={partnerId}
                                        setValue={(e) => {setPartnerId(e)}}
                                        type='FULL'
                                    />
                                </View>

                                <View style={{ marginTop: 20, zIndex: 5000 }}>
                                    <CustomDropDownWithIcons
                                    placeholder={t('common:managerName')}
                                        defValue={managerId}
                                        options={managerOptions}
                                        value={managerId}
                                        setValue={(e) => {setManagerId(e)}}
                                        type='FULL'
                                    />
                                </View>
                                {/* Save/Cancel Buttons */}
                                <SafeAreaView
                                    style={[
                                        styles.buttons,
                                        styles.buttonsContainer,
                                        styles.functionalityButtons,
                                    ]}
                                >
                                    <CustomButton
                                        text={isEditing ? t("common:update") : t("common:save")}
                                        onPress={handleSaveOrUpdate}
                                        type="BUTTON_SAVE"
                                        sz={15}
                                        style={styles.buttons}
                                        isGradient={true}
                                    />
                                    <CustomButton
                                        text={t("common:cancel")}
                                        onPress={handleCancel}
                                        type="BUTTON_CANCEL"
                                        sz={15}
                                        style={styles.buttons}
                                    />
                                </SafeAreaView>
                            </Card.Content>
                        </Card>
                    </ScrollView>
                    {/* Right column */}
                    <View style={{ flexDirection: 'column', width: '70%' }}>
                        <ScrollView style={Platform.OS === 'web' && width > 1200 ? styles.rightColumnNormal : styles.rightColumn} >
                            {/* Render clients */}
                            <FlatList
                                data={pagedItems}
                                keyExtractor={item => item.phasecode}
                                renderItem={renderProjects}
                                ListEmptyComponent={
                                    <CustomLabel value={t('common:noprojects')} type="MINORTITLE" />
                                }
                                contentContainerStyle={{ padding: 12 }}
                            />
                        </ScrollView>
                        {/* Show Paging Menu */}
                        <CustomPaging
                            items={clientProjects}
                            itemsPerPage={20}
                            onPageItemsChange={setPagedItems}
                        />
                    </View>
                </SafeAreaView>
            </SafeAreaView>
        </SafeAreaView>
    );
}

const makeStyles = (colors, width, height) =>
    StyleSheet.create({
        main: {
            flex: 1,
            flexDirection: 'column',
            marginTop: "1%",
            marginLeft: '2%',
            marginRight: '2%',
        },
        scroll: {
            flex: 1,
        },
        leftColumn: {
            flex: 2,
            marginLeft: Platform.OS === 'web' ? '2%' : '1%',
            maxWidth: width * 0.25,
            marginRight: Platform.OS === 'web' ? 20 : '1%',
            marginTop: height < 600 ? height * 40 / 100 : 10
        },
        rightColumnNormal: {
            flex: 1,
            marginTop: 10,
        },
        rightColumn: {
            width: width * 95 / 100,
            marginTop: height < 600 ? height * 40 / 100 : 10,
        },
        cardstyle: {
            marginLeft: (Platform.OS === 'web' && width > 1200 ? '2%' : '4%'),
            marginRight: (Platform.OS === 'web' && width > 1200 ? '2%' : '4%'),
            marginTop: 10,
            marginBottom: 10,
            borderRadius: 5,
            elevation: 3,
            shadowColor: colors.card_line,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.1,
            shadowRadius: 5,
            backgroundColor: colors.background_card,
            flex: 'none',
        },
        cardstyleOpen: {
            marginLeft: (Platform.OS === 'web' && width > 1200 ? '2%' : '4%'),
            marginRight: (Platform.OS === 'web' && width > 1200 ? '2%' : '4%'),
            marginTop: 10,
            marginBottom: 10,
            borderRadius: 5,
            borderWidth: 1,
            borderColor: colors.buttons_primary,
            elevation: 3,
            shadowColor: colors.card_line,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.1,
            shadowRadius: 5,
            backgroundColor: colors.background_card,
            flex: 'none',
        },
        row: {
            flex: 1,
            flexDirection: "row",
        },
        buttons: {
            flexDirection: "row",
            justifyContent: "flex-end",
            alignSelf: "flex-end",
        },
        buttonsContainer: {
            flexDirection: "row",
            justifyContent: "right",
            padding: 0,
            marginTop: 20
        },
        functionalityButtons: {
            justifyContent: "flex-end",
        },
        CheckBoxRow: {
            flexDirection: "row",
            marginTop: 15,
            marginRight: 15,
        },
        line: {
            borderBottomColor: colors.buttons_primary,
            borderBottomWidth: StyleSheet.hairlineWidth,
            marginTop: "1%",
            marginBottom: "1%",
        },
        backButton: {
            alignSelf: 'flex-end',
            marginBottom: 5,
            padding: 5,
        },
        showHide: {
            borderRadius: 35,
            backgroundColor: colors.background_card,
        },
        details: {
            flex: 1,
            margin: '2%',
        },
        item: {
            flex: 1,
            padding: 10,
        },
    });

export default HRAdminClientProjectDetails;
